import { Statsig } from 'src/sdk/statsig'
import type { KitlookData } from 'src/types/kitlooks'
import { generateSectionId } from 'src/utils/generateSectionId'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { InView } from 'react-intersection-observer'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'

import { KITLOOK_WIDGET_EXPERIMENT_ID } from '../common/StatsigExposure/StatsigExposure'
import { KitlookWidgetResume } from './components/KitlookResume'
import type { KitlookResumeOverrides } from './components/KitlookResume'
import { KitlookProvider } from './context/KitlookContext'
import { KitlookCarousel } from './components/KitlookCarousel'
import { useGlobalContext } from '../contexts/GlobalContext'

interface KitlookWidgetContentProps {
  kitlooks: KitlookData[]
  productId: string
  resumeOverrides?: KitlookResumeOverrides
  variant?: 'default' | 'collectionCMS'
}

export function KitlookWidgetContent({
  kitlooks,
  productId,
  resumeOverrides,
  variant = 'default',
}: KitlookWidgetContentProps) {
  const { pageData } = useGlobalContext()
  const standardObj = getStandardObject(pageData)

  const KITLOOK_WIDGET_SECTION_ID = generateSectionId(
    'kitlook_section',
    productId
  )

  if (kitlooks.length === 0) {
    return null
  }

  const kitLookViewed = () => {
    dispatchAmplitudeEvent({
      eventName: 'Product List Viewed',
      eventData: {
        ...standardObj,
        'product list type': 'vitrine',
        'product list name': 'Kit Widget',
      },
    })
  }

  return (
    <InView
      as="div"
      className={variant === 'collectionCMS' ? 'h-full' : undefined}
      onChange={(inView) => {
        if (inView) {
          kitLookViewed()
        }
      }}
      triggerOnce
    >
      <KitlookProvider kitlooks={kitlooks}>
        {variant === 'collectionCMS' ? (
          <div id={KITLOOK_WIDGET_SECTION_ID} className="w-full h-full">
            <KitlookWidgetResume overrides={resumeOverrides} hideKitInfo />
          </div>
        ) : (
          <div
            id={KITLOOK_WIDGET_SECTION_ID}
            className="max-w-[1280px] mx-auto flex mb-10 md:mb-0 px-4 mt-4 md:mt-0 md:px-0"
          >
            <KitlookWidgetResume overrides={resumeOverrides} />
            <div className="hidden md:block md:min-w-0 md:flex-1">
              <KitlookCarousel />
            </div>
          </div>
        )}
      </KitlookProvider>
    </InView>
  )
}

interface KitlookWidgetParams {
  kitlooks: KitlookData[]
  productId: string
}

export function KitlookWidget({ kitlooks, productId }: KitlookWidgetParams) {
  const isEnabled = Statsig.useStatsigFeatureGateValue(
    KITLOOK_WIDGET_EXPERIMENT_ID
  )

  if (!isEnabled) {
    return null
  }

  return <KitlookWidgetContent kitlooks={kitlooks} productId={productId} />
}
